import { Report } from '@aipb/gulliver/castor/model';
import { CatalogEnvService } from '@aipb/gulliver/catalog/env';
import {
  CATALOG_DETAIL_LOCAL_STORAGE_KEY,
  CustomRecordItem,
  DetailItem,
  DetailTab
} from '@aipb/gulliver/catalog/model';
import { createDetailCompositeKey } from '@aipb/gulliver/catalog/util';
import { AppConfigId } from '@aipb/gulliver/env/model';
import { APP_NAME } from '@aipb/gulliver/env/util';
import { ReportItemBody } from '@aipb/gulliver/report/model';
import { selectLoading, updateLoading, withLoading } from '@aipb/shared/util/state';
import { inject, Injectable } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import {
  deleteAllEntities,
  deleteEntities,
  getEntity,
  hasEntity,
  selectAllEntities,
  selectEntity,
  updateEntities,
  upsertEntities,
  withEntities
} from '@ngneat/elf-entities';
import { excludeKeys, localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

interface DetailProps {
  ui: {
    [configId in AppConfigId]: {
      tabsLocked: boolean;
      showHeader: boolean;
      activeTabIndex: number;
      tabs: DetailTab[];
      compactHeader: boolean;
    };
  };
}

function initDetailProps(catalogEnv: CatalogEnvService): DetailProps {
  const configIds = catalogEnv.getConfigIds();

  const ui = {};

  for (const configId of configIds) {
    ui[configId] = {
      tabsLocked: catalogEnv.getEnv(configId).defaultLockDetailTabs,
      activeTabIndex: -1,
      tabs: [],
      showHeader: true
    };
  }

  return {
    ui
  };
}

@Injectable({
  providedIn: 'root'
})
export class DetailRepository {
  private appName = inject(APP_NAME);
  private catalogEnv = inject(CatalogEnvService);
  private store = createStore(
    { name: 'catalog-detail' },
    withEntities<DetailItem>(),
    withProps<DetailProps>(initDetailProps(this.catalogEnv)),
    withLoading()
  );
  persist = persistState(this.store, {
    key: `${this.appName}-${CATALOG_DETAIL_LOCAL_STORAGE_KEY}`,
    storage: localStorageStrategy,
    source: () => this.store.pipe(excludeKeys(['loading', 'entities', 'ids']))
  });
  loading$ = this.store.pipe(selectLoading());
  items$ = this.store.pipe(selectAllEntities());
  tabsLocked$ = this.store.pipe(select((state) => state.ui[this.configId].tabsLocked));
  activeTabIndex$ = this.store.pipe(select((state) => state.ui[this.configId].activeTabIndex));
  tabs$ = this.store.pipe(select((state) => state.ui[this.configId].tabs));
  showHeader$ = this.store.pipe(select((state) => state.ui[this.configId].showHeader));
  compactHeader$ = this.store.pipe(select((state) => state.ui[this.configId].compactHeader));

  get configId() {
    return this.catalogEnv.activeConfigId;
  }

  selectDetail(id: string) {
    return this.store.pipe(selectEntity(id));
  }

  hasDetail(id: string) {
    return this.store.query(hasEntity(id));
  }

  selectIsOpened(coreId: string, item: CustomRecordItem): Observable<boolean> {
    return combineLatest([this.activeTabIndex$, this.tabs$]).pipe(
      map((data) =>
        data[1].some((tab) => tab.id === createDetailCompositeKey(coreId, item[this.catalogEnv.env.castor.UUID]))
      )
    );
  }

  selectIsActive(coreId: string, item: CustomRecordItem): Observable<boolean> {
    return combineLatest([this.activeTabIndex$, this.tabs$]).pipe(
      map((data) => {
        const tab = data[1].find(
          (t) => t.id === createDetailCompositeKey(coreId, item[this.catalogEnv.env.castor.UUID])
        );

        if (!tab) return false;

        const activeTab = this.getActiveTab();

        return activeTab && activeTab.id === tab.id;
      })
    );
  }

  public setTabsLocked(tabsLocked = true): void {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          tabsLocked
        }
      }
    }));
  }

  addDetail(detail: DetailItem) {
    this.store.update(upsertEntities(detail));
  }

  removeDetails() {
    this.store.update(deleteAllEntities());
  }

  updateReport(id: string, report: Report, lang: string, data: ReportItemBody) {
    this.store.update(
      updateEntities(id, (entity) => ({
        ...entity,
        reports: {
          ...entity.reports,
          [report.reportId]: {
            ...entity.reports[report.reportId],
            [lang]: data
          }
        }
      }))
    );
  }

  removeTabs() {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          tabs: []
        }
      }
    }));
  }

  removeDetail(id: string): void {
    this.store.update(deleteEntities(id));
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          tabs: state.ui[this.configId].tabs.filter((t) => t.id !== id)
        }
      }
    }));
  }

  setCompactHeader(compactHeader: boolean) {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          compactHeader
        }
      }
    }));
  }

  addTab(tab: DetailTab) {
    this.store.update((state) => {
      const tabs = state.ui[this.configId].tabs;

      if (state.ui[this.configId].tabsLocked) {
        tabs.pop();
      }

      tabs.push(tab);

      return {
        ...state,
        ui: {
          ...state.ui,
          [this.configId]: {
            ...state.ui[this.configId],
            tabs
          }
        }
      };
    });
  }

  setActiveTabIndex(activeTabIndex: number) {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        [this.configId]: {
          ...state.ui[this.configId],
          activeTabIndex
        }
      }
    }));
  }

  getActiveTabIndex(): number {
    return this.store.getValue().ui[this.configId].activeTabIndex;
  }

  getActiveTab(): DetailTab {
    const i = this.getActiveTabIndex();

    if (i >= 0 && i < this.getTabCount()) {
      return this.getTab(i);
    }

    return null;
  }

  hasTab(id: string): boolean {
    return this.getTabs().some((t) => t.id === id);
  }

  getDetail(id: string): DetailItem {
    return this.store.query(getEntity(id));
  }

  getTabs(): DetailTab[] {
    return this.store.getValue().ui[this.configId].tabs;
  }

  getTab(i: number) {
    return this.getTabs()[i];
  }

  getTabCount(): number {
    return this.getTabs().length;
  }

  setLoading(loading: boolean) {
    this.store.update(updateLoading(loading));
  }

  reset() {
    this.store.reset();
  }
}
